import type {
  AuthResponse,
  FAQItem,
  Lead,
  LoginCredentials,
  SolutionArea,
  KnowledgeSource,
  KnowledgeDocument,
  SearchRequest,
  SearchResult,
} from '../types'

const API_BASE_URL = '/api'

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    const message = data.message || data.error || `Error ${response.status}: ${response.statusText}`
    throw new Error(message)
  }
  return response.json() as Promise<T>
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const token = localStorage.getItem('token')
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  const selectedTenantId = localStorage.getItem('selectedTenantId')
  if (selectedTenantId) {
    headers['X-Tenant-Id'] = selectedTenantId
  }
  return headers
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    headers: getHeaders(),
  })
  return handleResponse<T>(response)
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<T>(response)
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<T>(response)
}

async function del<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: getHeaders(),
  })
  return handleResponse<T>(response)
}

export const authApi = {
  login: (credentials: LoginCredentials) => post<AuthResponse>('/auth/login', credentials),
}

export const leadsApi = {
  getAll: async () => {
    const response = await get<{ items: Lead[]; totalCount: number; page: number; pageSize: number }>('/leads')
    return response.items
  },
  create: (lead: Omit<Lead, 'id' | 'createdAt'>) => post<Lead>('/leads', lead),
}

export const faqsApi = {
  getAll: () => get<FAQItem[]>('/faqs'),
  create: (faq: Omit<FAQItem, 'id'>) => post<FAQItem>('/faqs', faq),
  update: (id: string, faq: Omit<FAQItem, 'id'>) => put<FAQItem>(`/faqs/${id}`, faq),
  delete: (id: string) => del<void>(`/faqs/${id}`),
}

export const areasApi = {
  getAll: () => get<SolutionArea[]>('/areas'),
  update: (id: string, area: Partial<SolutionArea>) => put<SolutionArea>(`/areas/${id}`, area),
}

export const knowledgeApi = {
  getSources: () => get<KnowledgeSource[]>('/knowledge/sources'),
  createSource: (source: Omit<KnowledgeSource, 'id' | 'status' | 'createdAt' | 'updatedAt'>) =>
    post<KnowledgeSource>('/knowledge/sources', source),
  updateSource: (id: string, source: Partial<KnowledgeSource>) => put<KnowledgeSource>(`/knowledge/sources/${id}`, source),
  deleteSource: (id: string) => del<void>(`/knowledge/sources/${id}`),

  getDocuments: () => get<KnowledgeDocument[]>('/knowledge/documents'),
  getDocument: (id: string) => get<KnowledgeDocument & { content: string }>(`/knowledge/documents/${id}`),
  uploadDocument: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    const token = localStorage.getItem('token')
    const tenantId = localStorage.getItem('selectedTenantId')
    const headers: Record<string, string> = {}
    if (token) headers.Authorization = `Bearer ${token}`
    if (tenantId) headers['X-Tenant-Id'] = tenantId

    const response = await fetch(`${API_BASE_URL}/knowledge/documents/upload`, {
      method: 'POST',
      headers,
      body: formData,
    })
    return handleResponse<KnowledgeDocument>(response)
  },
  deleteDocument: (id: string) => del<void>(`/knowledge/documents/${id}`),

  search: (request: SearchRequest) => post<SearchResult[]>('/knowledge/search', request),
}
